import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { DEPARTMENTS } from '@cg-idea/common';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import axios from 'axios';
import {
  Send,
  Paperclip,
  Loader2,
  Trash2,
  CheckCircle2,
  Check,
  Search,
  User,
  Building2,
  Phone,
  Hash,
  Sparkles,
  ChevronDown
} from 'lucide-react';

interface AttachmentItem {
  fileName: string;
  fileKey: string;
  fileSize: number;
  fileType: string;
}

export function NewIdea({ isEdit = false }: { isEdit?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = !!localStorage.getItem('accessToken');

  // Employee autocomplete states
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  // Department searchable autocomplete states
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const deptDropdownRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Initializing state directly from localStorage draft if present
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(() => {
    if (isEdit) return null;
    const saved = localStorage.getItem('ideaFormDraft');
    if (saved) {
      try { return JSON.parse(saved).selectedEmployee || null; } catch { return null; }
    }
    return null;
  });

  const [title, setTitle] = useState<string>(() => {
    if (isEdit) return '';
    const saved = localStorage.getItem('ideaFormDraft');
    if (saved) {
      try { return JSON.parse(saved).title || ''; } catch { return ''; }
    }
    return '';
  });


  const [department, setDepartment] = useState<string>('');

  const [deptSearchQuery, setDeptSearchQuery] = useState<string>('');
  const [category, setCategory] = useState<string>('General');
  const [expectedBenefits, setExpectedBenefits] = useState<string>('Refer to description');

  const [costSaving, setCostSaving] = useState<string>(() => {
    if (isEdit) return '';
    const saved = localStorage.getItem('ideaFormDraft');
    if (saved) {
      try { return JSON.parse(saved).costSaving || ''; } catch { return ''; }
    }
    return '';
  });

  const [description, setDescription] = useState<string>(() => {
    if (isEdit) return '';
    const saved = localStorage.getItem('ideaFormDraft');
    if (saved) {
      try { return JSON.parse(saved).description || ''; } catch { return ''; }
    }
    return '';
  });

  const [attachments, setAttachments] = useState<AttachmentItem[]>(() => {
    if (isEdit) return [];
    const saved = localStorage.getItem('ideaFormDraft');
    if (saved) {
      try { return JSON.parse(saved).attachments || []; } catch { return []; }
    }
    return [];
  });

  // UX states
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isFetchingIdea, setIsFetchingIdea] = useState(isEdit);

  // Save form draft to localStorage
  useEffect(() => {
    if (!submitSuccess && !isEdit) {
      const draft = {
        selectedEmployee,
        title,
        costSaving,
        description,
        attachments,
      };
      try {
        localStorage.setItem('ideaFormDraft', JSON.stringify(draft));
      } catch (e) {
        console.warn('Failed to save draft to localStorage (quota exceeded)');
      }
    }
  }, [selectedEmployee, title, department, costSaving, description, attachments, submitSuccess, isEdit]);

  // Close department dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target as Node)) {
        setShowDeptDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prefill deptSearchQuery when department state changes
  useEffect(() => {
    if (department) {
      setDeptSearchQuery(department);
    }
  }, [department]);

  // Fetch idea details for editing
  useEffect(() => {
    if (isEdit && id) {
      setIsFetchingIdea(true);
      apiClient.get(`/ideas/${id}`)
        .then((res) => {
          const idea = res.data.data;
          setTitle(idea.title || '');
          setDescription(idea.description || '');
          setDepartment(idea.department || '');
          setCategory(idea.category || 'General');
          setExpectedBenefits(idea.expectedBenefits || 'Refer to description');
          setCostSaving(idea.costSaving ? String(idea.costSaving) : '');
          setAttachments(idea.attachments || []);
          if (idea.employee) {
            setSelectedEmployee({
              name: idea.employee.name,
              email: idea.employee.email,
              cgId: idea.employee.cgId,
              department: idea.employee.department,
              phoneNumber: idea.employee.phoneNumber,
            });
          }
        })
        .catch((err) => {
          setErrorMessage('Failed to load idea details for editing.');
        })
        .finally(() => {
          setIsFetchingIdea(false);
        });
    } else {
      setIsFetchingIdea(false);
    }
  }, [isEdit, id]);

  // Prefill employee if authenticated (only if not editing)
  useEffect(() => {
    if (isAuthenticated && !isEdit) {
      const loggedInEmployee = {
        name: localStorage.getItem('userName') || 'Employee',
        email: localStorage.getItem('userEmail') || '',
        cgId: localStorage.getItem('mpId') || 'N/A',
        darwinId: 'N/A',
        department: localStorage.getItem('userDepartment') || '',
        phoneNumber: '', // optional
      };
      setSelectedEmployee(loggedInEmployee);
    }
  }, [isAuthenticated, isEdit]);

  // Autocomplete fetch effect
  useEffect(() => {
    if (selectedEmployee || searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiClient.get(`/employees/search?q=${searchQuery}`);
        setSuggestions(res.data.data);
      } catch (err) {
        console.error('Error searching employees:', err);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedEmployee]);

  // File upload logic (S3 upload helper)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    setUploadingFiles(true);
    setErrorMessage('');

    const files = Array.from(e.target.files);
    const uploadedList = [...attachments];

    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        setErrorMessage(`File ${file.name} exceeds the maximum size limit of 50 MB`);
        continue;
      }

      try {
        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: {
            fileName: file.name,
            contentType: file.type,
          },
        });
        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': file.type },
        });
        uploadedList.push({
          fileName: file.name,
          fileKey: fileKey,
          fileSize: file.size,
          fileType: file.type,
        });
      } catch (err) {
        setErrorMessage(`Failed to upload ${file.name}.`);
      }
    }
    setAttachments(uploadedList);
    setUploadingFiles(false);
  };

  const removeAttachment = (key: string) => {
    setAttachments(attachments.filter((item) => item.fileKey !== key));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) {
      setErrorMessage('Please select a valid employee.');
      return;
    }
    if (!department) {
      setErrorMessage('Please select the department where this idea will be implemented.');
      return;
    }
    if (!title.trim()) {
      setErrorMessage('Please enter a name for your idea.');
      return;
    }
    if (!description.trim()) {
      setErrorMessage('Please enter a description for your idea.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    // Format and clean indicative savings
    const cleanedSavings = costSaving
      ? parseFloat(costSaving.replace(/,/g, ''))
      : null;

    if (cleanedSavings !== null && cleanedSavings > 9999999999.99) {
      setErrorMessage('Indicative saving amount is too large');
      setLoading(false);
      return;
    }

    const payload = {
      employeeEmail: selectedEmployee.email,
      title,
      department,
      category,
      expectedBenefits,
      costSaving: isNaN(cleanedSavings as any) ? null : cleanedSavings,
      description,
      attachments,
    };

    try {
      if (isEdit && id) {
        await apiClient.put(`/ideas/${id}`, payload, { params: { submit: true } });
      } else {
        await apiClient.post('/ideas/public', payload);
      }
      localStorage.removeItem('ideaFormDraft');
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      const resError = err.response?.data?.error;
      let msg = resError?.message || 'An error occurred while submitting your proposal.';

      if (resError?.code === 'VALIDATION_ERROR' && resError?.details) {
        if (Array.isArray(resError.details)) {
          msg = resError.details.map((d: any) => d.message).join(' | ');
        } else {
          const detailKeys = Object.keys(resError.details);
          if (detailKeys.length > 0) {
            msg = detailKeys.map(k => `${k}: ${resError.details[k].join(', ')}`).join(' | ');
          }
        }
      }
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSearchQuery('');
    setSuggestions([]);
    if (!isAuthenticated) {
      setSelectedEmployee(null);
    }
    setDepartment('');
    setDeptSearchQuery('');
    setTitle('');
    setCostSaving('');
    setDescription('');
    setAttachments([]);
    setErrorMessage('');
    setSubmitSuccess(false);
    setShowDeptDropdown(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    localStorage.removeItem('ideaFormDraft');
  };

  // 1. Success confirmation screen (Guest Success Screen)
  if (submitSuccess) {
    return (
      <Layout title={isEdit ? "Idea Updated" : "Proposal Submitted"}>
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center mt-8 animate-in fade-in duration-300">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">{isEdit ? 'Updated Successfully!' : 'Thank you!'}</h2>
          <p className="text-slate-600 mb-6">
            {isEdit
              ? 'Your idea has been successfully updated.'
              : 'Your idea has been successfully submitted for moderation.'}
          </p>

          <div className="bg-slate-50 rounded-xl p-5 mb-8 text-left border border-slate-100 space-y-3">
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <User className="w-4 h-4 text-slate-400" />
              <span><strong>Submitter:</strong> {selectedEmployee?.name}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span><strong>Implementation Department:</strong> {department}</span>
            </div>
            {selectedEmployee?.phoneNumber && (
              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Phone className="w-4 h-4 text-slate-400" />
                <span><strong>Phone Number:</strong> {selectedEmployee.phoneNumber}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isEdit && (
              <button
                onClick={resetForm}
                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-sm transition-all cursor-pointer border-0"
              >
                Submit Another Idea
              </button>
            )}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="px-5 py-2.5 bg-[#1C4ED8] hover:bg-blue-700 text-white font-semibold rounded-lg text-sm text-center transition-all shadow-sm"
              >
                Sign In to Track Status
              </Link>
            )}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 bg-[#1C4ED8] hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-all shadow-sm border-0"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  if (isFetchingIdea) {
    return (
      <Layout title="Modify Idea">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
          <div className="flex flex-col items-center justify-center py-32 space-y-4">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <p className="text-slate-500 font-medium">Loading idea details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const hasFormInput = Boolean(
    title.trim().length > 0 ||
    description.trim().length > 0 ||
    costSaving.trim().length > 0 ||
    attachments.length > 0 ||
    (!isAuthenticated && selectedEmployee !== null) ||
    (!isAuthenticated && searchQuery.trim().length > 0) ||
    (!isAuthenticated && department.trim().length > 0) ||
    (!isAuthenticated && deptSearchQuery.trim().length > 0) ||
    (isAuthenticated && department !== (localStorage.getItem('userDepartment') || '')) ||
    (isAuthenticated && deptSearchQuery !== (localStorage.getItem('userDepartment') || ''))
  );

  return (
    <Layout title={isEditMode() ? 'Modify Idea' : ''}>
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
        <div className="p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 font-display">Submit Your Idea</h2>
            {/* <p className="text-slate-500 text-sm mt-1">Have an innovative idea? Submit it below to share it with our teams.</p> */}
          </div>

          <form onSubmit={handleFormSubmit} className="space-y-6">
            {/* 1. Submitter Search / Autocomplete */}
            <div>
              <label className="block text-slate-700 text-sm font-semibold mb-2">
                Employee email ID *
              </label>
              {selectedEmployee ? (
                <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-bold text-blue-900 text-base flex items-center">
                      <User className="w-4 h-4 mr-2 text-blue-500 shrink-0" />
                      {selectedEmployee.name}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-blue-700 mt-1">
                      {selectedEmployee.email && (
                        <span className="bg-blue-100/60 px-2 py-0.5 rounded flex items-center">
                          {selectedEmployee.email}
                        </span>
                      )}
                      {selectedEmployee.cgId && (
                        <span className="bg-blue-100/60 px-2 py-0.5 rounded flex items-center">
                          <Hash className="w-3 h-3 mr-1" /> DOM ID: {selectedEmployee.cgId}
                        </span>
                      )}
                      {selectedEmployee.department && (
                        <span className="bg-blue-100/60 px-2 py-0.5 rounded flex items-center">
                          <Building2 className="w-3 h-3 mr-1" /> {selectedEmployee.department}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isAuthenticated && (
                    <button
                      type="button"
                      onClick={() => setSelectedEmployee(null)}
                      className="text-xs font-semibold text-blue-600 hover:text-red-500 bg-transparent border-0 cursor-pointer"
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                    <Search className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    placeholder="Enter your email ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-3 border border-slate-300 rounded-xl text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                  />
                  {searching && (
                    <span className="absolute right-3 top-3.5">
                      <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                    </span>
                  )}

                  {/* Autocomplete suggestions list */}
                  {suggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100 animate-in slide-in-from-top-1 duration-150">
                      {suggestions.map((emp) => (
                        <div
                          key={emp.id}
                          onClick={() => {
                            setSelectedEmployee(emp);
                            setSearchQuery('');
                            setSuggestions([]);
                          }}
                          className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col justify-between"
                        >
                          <p className="font-semibold text-slate-800 text-sm">{emp.name}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {emp.cgId && (
                              <span className="bg-slate-100 text-[10px] text-slate-600 px-1.5 py-0.5 rounded font-mono">
                                DOM ID: {emp.cgId}
                              </span>
                            )}
                            {emp.email && (
                              <span className="bg-slate-100 text-[10px] text-slate-600 px-1.5 py-0.5 rounded font-mono">
                                {emp.email}
                              </span>
                            )}
                            {emp.department && (
                              <span className="bg-slate-100 text-[10px] text-slate-600 px-1.5 py-0.5 rounded">
                                {emp.department}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Idea Title */}
            <div>
              <label className="block text-slate-700 text-sm font-semibold mb-2">
                Name of idea *
              </label>
              <input
                type="text"
                placeholder="Enter a short title for your idea..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
              />
            </div>

            {/* 2. Department Dropdown (Searchable) */}
            <div ref={deptDropdownRef} className="relative">
              <label className="block text-slate-700 text-sm font-semibold mb-2">
                Department where idea to be implemented *
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search and select implementation department..."
                  value={deptSearchQuery}
                  onChange={(e) => {
                    setDeptSearchQuery(e.target.value);
                    setDepartment(''); // Clear selection if typing
                    setShowDeptDropdown(true);
                  }}
                  onFocus={() => setShowDeptDropdown(true)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-[#1C4ED8] focus:ring-1 focus:ring-[#1C4ED8] outline-none transition-all cursor-pointer"
                />
                <span className="absolute right-4 top-3.5 text-slate-400 pointer-events-none">
                  <ChevronDown className="w-5 h-5" />
                </span>

                {/* Autocomplete matching list */}
                {showDeptDropdown && (
                  <div className="absolute z-20 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100 animate-in slide-in-from-top-1 duration-150">
                    {(department && department === deptSearchQuery
                      ? DEPARTMENTS
                      : DEPARTMENTS.filter(d => d.toLowerCase().includes(deptSearchQuery.toLowerCase()))
                    ).map((dept) => (
                      <div
                        key={dept}
                        onClick={() => {
                          setDepartment(dept);
                          setDeptSearchQuery(dept);
                          setShowDeptDropdown(false);
                        }}
                        className={`px-4 py-2.5 transition-colors cursor-pointer text-sm font-medium ${department === dept
                          ? 'bg-blue-50 text-[#1C4ED8]'
                          : 'text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        {dept}
                      </div>
                    ))}
                    {!(department && department === deptSearchQuery) && DEPARTMENTS.filter(d =>
                      d.toLowerCase().includes(deptSearchQuery.toLowerCase())
                    ).length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-400 italic text-center">
                          No matching departments found.
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>

            {/* 3. Indicative Saving (Optional) */}
            <div>
              <label className="block text-slate-700 text-sm font-semibold mb-2">
                Indicative saving (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. 50,000"
                value={costSaving}
                onChange={(e) => setCostSaving(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
              />
            </div>

            {/* 4. Description */}
            <div>
              <label className="block text-slate-700 text-sm font-semibold mb-2">
                Description of the idea *
              </label>
              <textarea
                rows={6}
                placeholder="Provide details about your proposed idea. There are no character length restrictions..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
              ></textarea>
            </div>

            {/* 5. Document Upload (Optional) */}
            <div className="space-y-3">
              <label className="block text-slate-700 text-sm font-semibold mb-1">
                Document upload (Optional)
              </label>
              <div className="border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100/50 rounded-xl p-6 text-center transition-colors relative cursor-pointer group">
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <Paperclip className="w-8 h-8 text-slate-400 group-hover:text-blue-500 mx-auto mb-2" />
                <p className="text-sm text-slate-700 font-semibold">
                  Drag & drop files here or <span className="text-blue-600 underline">browse</span>
                </p>
                <p className="text-xs text-slate-400 mt-1.5">Max size per file: 50MB</p>
              </div>

              {uploadingFiles && (
                <div className="flex items-center justify-center space-x-2 py-3 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  <span>Uploading files...</span>
                </div>
              )}

              {attachments.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Attachments ({attachments.length})
                  </p>
                  <ul className="space-y-2">
                    {attachments.map((file) => (
                      <li
                        key={file.fileKey}
                        className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between shadow-sm"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="truncate text-sm text-slate-800 font-semibold">{file.fileName}</p>
                            <p className="text-xs text-slate-400">
                              {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(file.fileKey)}
                          className="p-1.5 text-slate-400 hover:text-red-500 transition-colors bg-transparent border-0 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded text-sm shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                {errorMessage}
              </div>
            )}

            {/* Actions */}
            <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
              {hasFormInput && !isEdit && (
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={loading || uploadingFiles}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-sm transition-all flex items-center space-x-2 cursor-pointer border-0 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4 shrink-0" />
                  <span>Clear Form</span>
                </button>
              )}
              <button
                type="submit"
                disabled={loading || uploadingFiles}
                className="px-6 py-3 bg-[#1C4ED8] hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-all shadow-sm flex items-center space-x-2 cursor-pointer border-0 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <Send className="w-4 h-4 shrink-0" />
                )}
                <span>Submit</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {!isAuthenticated && (
        <div className="max-w-2xl mx-auto mt-4 text-right px-2">
          <Link to="/login" className="text-[#1C4ED8] text-sm font-semibold hover:text-[#1d4ed8] transition-colors underline-offset-2 hover:underline">
            Login to track your submitted ideas
          </Link>
        </div>
      )}
    </Layout>
  );

  function isEditMode() {
    return false; // we disabled editing for guests in public layout
  }
}
